import DeckGL from "@deck.gl/react";
import { FlyToInterpolator, LinearInterpolator, MapView, WebMercatorViewport } from "@deck.gl/core";
import { setArcLayer, setCountryLabelsLayer, setCountryMapLayer, setScatterplotLayer } from "lib/map";
import classNames from "classnames";
import {
  getMobileMapDimensions,
  onChangeActiveCountry,
  onChangeActiveRegion,
  onChangeSelectedProduct,
  onUpdateProductsCountry,
  resetToWorldwideView,
  updateDeckLayers
} from "lib/map/functions";
import mapData from "lib/stores/mapData";
import { Map as StaticMap } from "react-map-gl";
import apiData from "lib/stores/apiData";
import context from "lib/stores/context";
import mapboxgl from "!mapbox-gl";
import { useEffect, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import styles from "styles/MapPane.module.scss";

const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const prevActiveCountry = null;
const flyTo = new FlyToInterpolator();
const linear = new LinearInterpolator();
const defaultPitch = 40;
const mapView = new MapView({
  controller: {
    doubleClickzoom: false,
    inertia: 0.1
  },
  repeat: true,
  fov: 10,
  scrollZoom: { speed: 0.01, smooth: true }
});

const MapPane = () => {
  const transitionDuration = 1000;
  const { hoveredPolygonIndex, year, selectedProduct, productsCountry, activeCategory, showProducts, viewState } = useSnapshot(mapData, {
    sync: true
  });
  const { activeView, activeCountry, activeRegion } = useSnapshot(context, {
    sync: true
  });
  const prevRef = useRef<{ timeout: number | null}>({ timeout: null });
  const { countries } = useSnapshot(apiData, { sync: true });
  const deckRef = useRef(null);
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const [layers, setLayers] = useState([setCountryMapLayer(activeCategory, hoveredPolygonIndex, activeCountry, activeView, year)]);

  useEffect(() => {
    if (prevRef.current.timeout !== null) {
      clearTimeout(prevRef.current.timeout);
      prevRef.current.timeout = null;
    }
    prevRef.current.timeout = setTimeout(() => {
      updateDeckLayers(setLayers, viewState, true);
    }, 500);
  }, [viewState]);

  useEffect(() => {
    if (window.innerHeight <= 768 && window.matchMedia("(orientation: portrait)").matches) {
      const el = document.getElementById("deckgl-wrapper-wrapper");
      const sidebarHeight = document.getElementById("sidebar-header").offsetHeight;
      const navbar = document.getElementById("toolbar-map-navigation").getBoundingClientRect();
      el.style.height = `${window.visualViewport.height - sidebarHeight - navbar.bottom}px`;
      el.style.position = "relative";
      el.style.top = `${navbar.bottom}px`;
    }
    if (window.innerWidth < 1600) {
      mapData.viewState.minZoom = -1.0;
    } else {
      mapData.viewState.minZoom = 2;
    }
    getMobileMapDimensions();
    const listener = () => {
      if (window.innerWidth < 1800) {
        mapData.viewState.minZoom = 0.75;
      } else {
        mapData.viewState.minZoom = 2;
      }
      getMobileMapDimensions();
    }
    window.addEventListener("resize", listener);
    return () => {
      window.removeEventListener("resize", listener);
    };
  }, []);

  useEffect(() => {
    onUpdateProductsCountry(deckRef, setLayers);
  }, [productsCountry, selectedProduct]);

  useEffect(() => {
    onChangeSelectedProduct(deckRef);
  }, [selectedProduct]);

  // Reset to Worldwide view
  useEffect(() => {
    resetToWorldwideView();
  }, [activeCountry, activeRegion]);
  useEffect(() => {
    if (!deckRef.current) return;
    onChangeActiveCountry(activeCountry, selectedProduct, prevActiveCountry, countries, mapData, linear, transitionDuration);
  }, [activeCountry, selectedProduct]);

  useEffect(() => {
    if (!deckRef.current || !deckRef.current.deck.width) return;
    onChangeActiveRegion(activeRegion, selectedProduct, activeCountry, mapData, transitionDuration, linear, deckRef.current.deck);
  }, [activeRegion, selectedProduct, activeCountry]);

  useEffect(() => {
    if (!deckRef.current) return;
    updateDeckLayers(setLayers, viewState, true);
  }, [hoveredPolygonIndex, selectedProduct, productsCountry, showProducts, activeCategory, activeView, activeCountry, year]);

  return (
    <div
      id="deckgl-container"
      ref={containerRef}
      className={classNames(styles.panel, styles["panel--map"], styles["overflow-hidden"])}
      style={{
        backgroundColor: "#333353"
      }}
    >
      <DeckGL
        id="deckgl-wrapper"
        getCursor={({ isDragging }, ...args) => {
          if (isDragging) return "grabbing";
          if (isHovering) return "pointer";
          return "grab";
        }}
        initialViewState={viewState}
        layers={layers}
        onHover={({ object }) => {
          setIsHovering(Boolean(object));
        }}
        onViewStateChange={({ viewState, interactionState }) => {
          if (window.innerWidth > 1023) {
            if (viewState.zoom < mapData.viewState.minZoom) {
              viewState.zoom = mapData.viewState.minZoom;
            }
            if (viewState.zoom < 1) {
              const factor = 1 - viewState.zoom;
              viewState.longitude = mapData.viewState.longitude * factor + viewState.longitude * (1 - factor);
              viewState.latitude = mapData.viewState.latitude * factor + viewState.latitude * (1 - factor);
            }
            return viewState;
          }
          return viewState;
        }}
        useDevicePixels={true}
        controller={true}
        ref={deckRef}
        transitionInterpolator={flyTo}
        onClick={(info, event) => {
          if (info.layer === null) {
            context.activeView = "worldwide";
            mapData.productsCountry = null;
            mapData.selectedProduct = null;
            context.activeCountry = null;
            context.activeRegion = null;
          }
        }}
        views={mapView}
      >
        <StaticMap
          ref={mapRef}
          mapLib={mapboxgl}
          reuseMaps
          styleDiffing
          mapStyle="mapbox://styles/minderoo/cli1cpdux000l01o887fmdl1m"
          attributionControl={false}
          mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        />
      </DeckGL>
    </div>
  );
};

export default MapPane;
